/* /assets/os-customer-care.js
 * Customer Information Hub rebuild (master brief, supersedes the
 * earlier hub JS -- the old "On this page" TOC/back-to-top builder is
 * gone entirely, not just switched off for one page, per the golden
 * rule against any in-page contents list anywhere in the hub).
 *
 * Four independent, self-contained pieces. Each finds its own target
 * elements by content pattern (text/href), not by relying on heading
 * ids -- the previous TOC script was what generated those ids in the
 * first place; removing it means raw Admin page.content has none.
 *
 *   1. Mobile "Jump to" dropdown -- navigates on change.
 *   2. Delivery returns callout -- clones the existing verbatim
 *      "returns" key facts + both portal buttons into a prominent
 *      block near the top of the page. Purely additive: the full
 *      Returns section still appears in its natural place further
 *      down, unchanged. Self-detecting (looks for the two known
 *      returns-portal hostnames), so it only ever does anything on a
 *      page that actually has them -- no page.handle dependency.
 *   3. Country type-ahead -- parses the existing delivery table
 *      (identified by its own header text, not a hardcoded page
 *      check) into a searchable list, hides the raw table, shows an
 *      accessible combobox instead. The table itself is untouched in
 *      the DOM (just visually hidden), so the underlying data stays
 *      in the one place Admin already edits it.
 *   4. Contact form -- native Shopify `contact` form submit (message
 *      delivery unchanged), and a conditional second silent request to
 *      Shopify's native `customer` form endpoint (same mechanism as
 *      snippets/newsletter-form.liquid already uses sitewide) only
 *      when the newsletter checkbox is ticked.
 *   5. Contact form -- enquiry type reaches the inbox: prepends
 *      "Enquiry type: X" (and "Order number: Y" if filled) onto
 *      contact[body] immediately before submit, since body is the one
 *      field Shopify's stock contact notification template always
 *      shows (custom field names like contact[enquiry_type] are not
 *      guaranteed to appear there otherwise).
 *   6. Delivery page -- rewords one stale sentence left over from
 *      before the country type-ahead (3, above) replaced the table it
 *      used to describe. Text-pattern match, same approach as 2 and 3,
 *      since {{ page.content }} is Admin-owned and can't be edited from
 *      this repo directly.
 *   7. accordion_item blocks (FAQ Q&A, Careers job listings) --
 *      theme.js's own accordion controller (assets/theme.js, the
 *      Accordion class) only self-registers against sections whose
 *      data-section-type is exactly "page-faq" or "accordion-single"
 *      (theme.sections.register calls near the bottom of that file).
 *      These blocks render inside sections/page-customer-care.liquid,
 *      data-section-type="page-customer-care", which theme.js has no
 *      registration for -- confirmed live the stock trigger/body markup
 *      alone does nothing on click without this. Reuses the exact same
 *      classes (accordion-is-open, accordion__body's display: none
 *      default -- assets/theme.css) so it looks and behaves like the
 *      stock component; only the open/close mechanism itself is
 *      reimplemented, self-contained, no dependency on theme.js's
 *      internal (non-exported) Accordion class.
 */
(function () {
  'use strict';

  /* ---------- 1. Mobile jump dropdown ---------- */
  function initJumpNav() {
    document.querySelectorAll('[data-customer-care-jump]').forEach(function (select) {
      select.addEventListener('change', function () {
        if (select.value) window.location.href = select.value;
      });
    });
  }

  /* ---------- 2. Delivery returns callout ---------- */
  function initReturnsCallout() {
    document.querySelectorAll('[data-customer-care-content]').forEach(function (content) {
      var ukLink = content.querySelector('a[href*="returnsportal.co"]');
      var intlLink = content.querySelector('a[href*="returngo.ai"]');
      if (!ukLink || !intlLink) return; // not the Delivery page's content

      var main = content.closest('.page-customer-care__main');
      if (!main || main.querySelector('.page-customer-care__returns-callout')) return;

      // The two buttons live in one <p>; the very next <p> after it is
      // the "must be returned within 14 days..." key-facts sentence.
      var buttonsPara = ukLink.closest('p');
      var factsPara = buttonsPara ? buttonsPara.nextElementSibling : null;

      var callout = document.createElement('div');
      callout.className = 'page-customer-care__returns-callout';

      var heading = document.createElement('h2');
      heading.textContent = 'Returns';
      callout.appendChild(heading);

      if (factsPara && factsPara.tagName === 'P') {
        var facts = document.createElement('p');
        facts.className = 'page-customer-care__returns-callout-facts';
        facts.innerHTML = factsPara.innerHTML;
        callout.appendChild(facts);
      }

      var buttons = document.createElement('div');
      buttons.className = 'page-customer-care__returns-callout-buttons';
      buttons.appendChild(ukLink.cloneNode(true));
      buttons.appendChild(intlLink.cloneNode(true));
      callout.appendChild(buttons);

      var h1 = main.querySelector('.page-customer-care__title');
      if (h1 && h1.nextSibling) {
        main.insertBefore(callout, h1.nextSibling);
      } else if (h1) {
        main.appendChild(callout);
      }
    });
  }

  /* ---------- 3. Country type-ahead ---------- */
  function findCountryTable(content) {
    var tables = content.querySelectorAll('table');
    for (var i = 0; i < tables.length; i++) {
      var headerText = tables[i].querySelector('tr') ? tables[i].querySelector('tr').textContent : '';
      if (/destination country/i.test(headerText)) return tables[i];
    }
    return null;
  }

  function initCountrySearch() {
    document.querySelectorAll('[data-customer-care-content]').forEach(function (content) {
      var table = findCountryTable(content);
      if (!table) return;

      var rows = Array.prototype.slice.call(table.querySelectorAll('tr')).slice(1); // drop header row
      var countries = rows.map(function (row) {
        var cells = row.querySelectorAll('td');
        return { name: (cells[0] && cells[0].textContent.trim()) || '', price: (cells[1] && cells[1].textContent.trim()) || '' };
      }).filter(function (c) { return c.name; });

      if (!countries.length) return;

      // Hide the raw table via JS only -- if this script never runs,
      // the table stays visible and fully usable, unchanged. Hides the
      // <table> itself directly rather than trying to find a
      // .rte__table-wrapper ancestor: that wrapper is added by
      // theme.js's own DOMContentLoaded handler (assets/theme.js,
      // "Target tables to make them scrollable"), and depending on
      // exactly when it runs relative to this script, .closest() can
      // find it or not -- confirmed live both ways produce an
      // identical visual result regardless, since that wrapper carries
      // no styling of its own beyond overflow:auto (assets/theme.css),
      // so it collapses to nothing once its only child is hidden. Hide
      // the table directly instead, which needs no assumption about
      // that other script's timing at all.
      table.setAttribute('hidden', '');

      var search = document.createElement('div');
      search.className = 'page-customer-care__country-search';

      var labelId = 'cc-country-label-' + Math.random().toString(36).slice(2, 8);
      var inputId = 'cc-country-input-' + Math.random().toString(36).slice(2, 8);
      var listboxId = 'cc-country-listbox-' + Math.random().toString(36).slice(2, 8);

      search.innerHTML =
        '<label id="' + labelId + '" for="' + inputId + '">Search for your country</label>' +
        '<div class="page-customer-care__country-search-field">' +
        '<input type="text" id="' + inputId + '" role="combobox" aria-expanded="false" aria-controls="' + listboxId + '" aria-autocomplete="list" aria-labelledby="' + labelId + '" autocomplete="off" placeholder="Start typing a country&hellip;">' +
        '<ul id="' + listboxId + '" class="page-customer-care__country-listbox" role="listbox" hidden></ul>' +
        '</div>' +
        '<div class="page-customer-care__country-result" aria-live="polite"></div>';

      table.parentNode.insertBefore(search, table);

      var input = search.querySelector('input');
      var listbox = search.querySelector('.page-customer-care__country-listbox');
      var result = search.querySelector('.page-customer-care__country-result');
      var activeIndex = -1;
      var currentMatches = [];

      function renderResult(country) {
        // James round 3: column headers were missing entirely (just a
        // bare "Country | Price" row) -- added a <thead>, wording
        // matched to the source table's own header cells ("Destination
        // country" / "Price", read from the row this script drops at
        // the top of this function) rather than inventing new terms
        // like "cost & time" the underlying data doesn't actually have
        // (no delivery-time column exists, only price).
        result.innerHTML =
          '<table class="page-customer-care__country-result-table">' +
          '<thead><tr><th scope="col">Country</th><th scope="col">Price</th></tr></thead>' +
          '<tbody><tr><th scope="row">' + country.name + '</th><td>' + country.price + '</td></tr></tbody>' +
          '</table>';
      }

      function closeList() {
        listbox.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
      }

      function setActive(index) {
        var options = listbox.querySelectorAll('[role="option"]');
        options.forEach(function (opt) { opt.classList.remove('is-active'); });
        if (options[index]) {
          options[index].classList.add('is-active');
          input.setAttribute('aria-activedescendant', options[index].id);
          activeIndex = index;
        }
      }

      function renderList(matches) {
        currentMatches = matches;
        activeIndex = -1;
        if (!matches.length) {
          listbox.innerHTML = '<li class="page-customer-care__country-empty">No matching country found</li>';
          listbox.hidden = false;
          input.setAttribute('aria-expanded', 'true');
          return;
        }
        listbox.innerHTML = matches.map(function (c, i) {
          return '<li role="option" id="' + listboxId + '-opt-' + i + '" data-index="' + i + '">' + c.name + '</li>';
        }).join('');
        listbox.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }

      function selectCountry(country) {
        input.value = country.name;
        renderResult(country);
        closeList();
      }

      input.addEventListener('input', function () {
        var q = input.value.trim().toLowerCase();
        result.innerHTML = '';
        if (!q) {
          closeList();
          return;
        }
        renderList(countries.filter(function (c) { return c.name.toLowerCase().indexOf(q) !== -1; }));
      });

      input.addEventListener('keydown', function (evt) {
        if (listbox.hidden && evt.key !== 'ArrowDown') return;
        if (evt.key === 'ArrowDown') {
          evt.preventDefault();
          if (listbox.hidden && input.value.trim()) {
            renderList(countries.filter(function (c) { return c.name.toLowerCase().indexOf(input.value.trim().toLowerCase()) !== -1; }));
          }
          if (currentMatches.length) setActive(Math.min(activeIndex + 1, currentMatches.length - 1));
        } else if (evt.key === 'ArrowUp') {
          evt.preventDefault();
          if (currentMatches.length) setActive(Math.max(activeIndex - 1, 0));
        } else if (evt.key === 'Enter') {
          if (activeIndex > -1 && currentMatches[activeIndex]) {
            evt.preventDefault();
            selectCountry(currentMatches[activeIndex]);
          }
        } else if (evt.key === 'Escape') {
          closeList();
        }
      });

      listbox.addEventListener('click', function (evt) {
        var li = evt.target.closest('[role="option"]');
        if (!li) return;
        var idx = parseInt(li.getAttribute('data-index'), 10);
        if (currentMatches[idx]) selectCountry(currentMatches[idx]);
      });

      document.addEventListener('click', function (evt) {
        if (!search.contains(evt.target)) closeList();
      });
    });
  }

  /* ---------- 4. Contact form: conditional newsletter opt-in ----------
   * The main form is a normal (non-AJAX) native Shopify `contact` form
   * submission -- see sections/page-contact.liquid for why. This just
   * rides alongside it: if the opt-in checkbox is ticked at the moment
   * of submit, fire a second, independent request to Shopify's native
   * `customer` form endpoint (the same mechanism snippets/newsletter-form.liquid
   * already uses sitewide: form_type=customer, contact[tags]=newsletter,
   * contact[accepts_marketing]=true) so the shopper is subscribed.
   * navigator.sendBeacon(), not fetch() -- sendBeacon is specifically
   * designed to survive the page navigating away, which happens here
   * at essentially the same moment as the main form's own native post.
   * Does NOT block or interfere with the main form submitting normally.
   */
  function initNewsletterOptIn() {
    var forms = document.querySelectorAll('[data-contact-form]');
    forms.forEach(function (form) {
      form.addEventListener('submit', function () {
        var checkbox = form.querySelector('[data-contact-newsletter-optin]');
        var emailField = form.querySelector('input[name="contact[email]"]');
        if (!checkbox || !checkbox.checked || !emailField || !emailField.value) return;

        var params = new URLSearchParams({
          form_type: 'customer',
          utf8: '✓',
          'contact[tags]': 'newsletter',
          'contact[accepts_marketing]': 'true',
          'contact[email]': emailField.value
        });
        // Explicit Blob with the correct content-type -- sendBeacon's
        // spec does not guarantee URLSearchParams is accepted directly,
        // and without application/x-www-form-urlencoded Shopify's own
        // form parser would not read the fields correctly.
        var body = new Blob([params.toString()], { type: 'application/x-www-form-urlencoded' });
        if (navigator.sendBeacon) {
          navigator.sendBeacon('/contact', body);
        }
      });
    });
  }

  /* ---------- 5. Contact form: guarantee enquiry type reaches the inbox ----------
   * Runs on the SAME submit event as initNewsletterOptIn, before the
   * native POST fires (both are plain 'submit' listeners on the same
   * form; this one mutates the body field's value, the other only
   * reads the email field, so order between them doesn't matter).
   */
  function initEnquiryTypeInBody() {
    var forms = document.querySelectorAll('[data-contact-form]');
    forms.forEach(function (form) {
      form.addEventListener('submit', function () {
        var enquiryField = form.querySelector('select[name="contact[enquiry_type]"]');
        var orderField = form.querySelector('input[name="contact[order_number]"]');
        var bodyField = form.querySelector('textarea[name="contact[body]"]');
        if (!enquiryField || !bodyField) return;

        var enquiryType = enquiryField.value;
        if (!enquiryType) return;

        var prefixLines = ['Enquiry type: ' + enquiryType];
        if (orderField && orderField.value.trim()) {
          prefixLines.push('Order number: ' + orderField.value.trim());
        }
        var prefix = prefixLines.join('\n') + '\n\n';

        // Idempotent: if the listener somehow ran twice (it won't --
        // submit fires once per real submission -- but this guards
        // against a resubmit-without-reload edge case) it will not
        // stack a second copy of the same prefix.
        if (bodyField.value.indexOf(prefix) !== 0) {
          bodyField.value = prefix + bodyField.value;
        }
      });
    });
  }

  /* ---------- 6. Delivery page: reword stale "table below" copy ---------- */
  function initDeliveryCopyFix() {
    var STALE = 'The table below is a guide; rates are subject to change.';
    var FIXED = 'Search for your country below for its delivery cost and time; rates are subject to change.';
    document.querySelectorAll('[data-customer-care-content] p').forEach(function (p) {
      if (p.textContent.indexOf(STALE) !== -1) {
        p.innerHTML = p.innerHTML.replace(STALE, FIXED);
      }
    });
  }

  /* ---------- 7. accordion_item blocks: independent open/close ---------- */
  function initCustomerCareAccordion() {
    document.querySelectorAll('.page-customer-care__accordion[data-accordion-group]').forEach(function (group) {
      var triggers = group.querySelectorAll('[data-accordion-trigger]');
      triggers.forEach(function (trigger) {
        var body = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!body) return;

        trigger.addEventListener('click', function () {
          var isOpen = trigger.classList.contains('accordion-is-open');

          triggers.forEach(function (otherTrigger) {
            if (otherTrigger === trigger) return;
            var otherBody = document.getElementById(otherTrigger.getAttribute('aria-controls'));
            otherTrigger.classList.remove('accordion-is-open');
            otherTrigger.setAttribute('aria-expanded', 'false');
            if (otherBody) otherBody.style.display = 'none';
          });

          if (isOpen) {
            trigger.classList.remove('accordion-is-open');
            trigger.setAttribute('aria-expanded', 'false');
            body.style.display = 'none';
          } else {
            trigger.classList.add('accordion-is-open');
            trigger.setAttribute('aria-expanded', 'true');
            body.style.display = 'block';
          }
        });
      });
    });
  }

  function init() {
    initJumpNav();
    initReturnsCallout();
    initCountrySearch();
    initNewsletterOptIn();
    initEnquiryTypeInBody();
    initDeliveryCopyFix();
    initCustomerCareAccordion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
